/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "semantic-ui-react";
import { TextField } from "@mui/material";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept, getCodeableConceptDisplayForObjectOrArray, simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { resourceUpdated, selectAllResources, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import Spinner from "../components/Spinner";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import { DisplayElementNotes, DisplayFromFHIRAnnotation } from "../datatypeComponents/AnnotationEntry";
import { DisplayFromFHIRRelatesTo } from "../datatypeComponents/RelatesToEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { DisplayFromFHIRCodeableReference } from "../datatypeComponents/CodeableReferenceEntry";
import { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import { displayStringFromFHIRRange } from "../datatypeComponents/RangeEntry";
import { DisplayFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import { InitialStatisticEntry } from "./editReferencedResourceComponents/EvidenceResourceEdit";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import CodeEntry from "../datatypeComponents/CodeEntry";
import SEVCO, { studyDesignCodeableConceptSet } from "../dictionaries/SEVCO";
import { ManageInclusionExclusionEnhancedCharacteristicTables } from "./editReferencedResourceComponents/GroupResourceEdit";
import generateNarrativeForEvidenceResource from "./generateNarrativeFunctions/generateNarrativeForEvidenceResource";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";


const editIterativeState = (state, stateSetter, newValue, parentField, field, indexTrail) => {
    stateSetter(prevState => {
        if (indexTrail.length > 0) {
            let element = state;
            for (let indexPosition in indexTrail) {
                let index = indexTrail[indexPosition];
                element = element[parentField][index];
            }
            element[field] = newValue;
            return {
                ...prevState,
                [parentField]: state[parentField]
            };
        } else {
            return {
                ...prevState,
                [field]: newValue,
            };
        }
    });
}

const retrieveIterativeElement = (element, parentField, field, indexTrail, fieldIndex, subField) => {
    let fieldValue = "";
    if (indexTrail?.length > 0 && element[parentField]) {
        fieldValue = retrieveIterativeElement(element[parentField][indexTrail[0]], parentField, field, indexTrail.slice(1), fieldIndex, subField);
    } else {
        if (fieldIndex !== undefined) {
            if (subField === undefined) {
                fieldValue = element[field][fieldIndex];
            }
        } else {
            fieldValue = element[field];
        }
    }
    return fieldValue;
}

const evidenceDotSynthesisTypeValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "std-MA", display: "summary data meta-analysis" },
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "IPD-MA", display: "individual patient data meta-analysis" },
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "indirect-NMA", display: "indirect network meta-analysis" },
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "combined-NMA", display: "combined direct plus indirect network meta-analysis" },
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "range", display: "range of results" },
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "classification", display: "classifcation of results" },
    { system: "http://terminology.hl7.org/CodeSystem/synthesis-type", code: "NotApplicable", display: "not applicable" }
];

const getVariableDefinitionMap = (evidenceJson, resourceCollection) => {
    if (!evidenceJson || evidenceJson.resourceType !== "Evidence" || !evidenceJson.variableDefinition || evidenceJson.variableDefinition.length === 0) {
        return null;
    }
    let variableDefinitionMap = {};
    for (let variableDefinitionIndex in evidenceJson.variableDefinition) {
        let variableDefinition = evidenceJson.variableDefinition[variableDefinitionIndex];
        if (variableDefinition.variableRole) {
            if (typeof variableDefinition.variableRole === "string") {

                if (variableDefinition.variableRole === "population") {
                    variableDefinitionMap.population = variableDefinition;
                    if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                        variableDefinitionMap.populationObservedReference = variableDefinition.observed.reference;
                        let thisfoi = variableDefinition.observed.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.populationObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                    if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                        variableDefinitionMap.populationIntendedReference = variableDefinition.intended.reference;
                        let thisfoi = variableDefinition.intended.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.populationIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                } else if (variableDefinition.variableRole === "exposure") {
                    variableDefinitionMap.exposure = variableDefinition;
                    if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                        variableDefinitionMap.exposureObservedReference = variableDefinition.observed.reference;
                        let thisfoi = variableDefinition.observed.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.exposureObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                    if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                        variableDefinitionMap.exposureIntendedReference = variableDefinition.intended.reference;
                        let thisfoi = variableDefinition.intended.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.exposureIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                } else if (variableDefinition.variableRole === "referenceExposure") {
                    variableDefinitionMap.referenceExposure = variableDefinition;
                    if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                        variableDefinitionMap.referenceExposureObservedReference = variableDefinition.observed.reference;
                        let thisfoi = variableDefinition.observed.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.referenceExposureObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                    if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                        variableDefinitionMap.referenceExposureIntendedReference = variableDefinition.intended.reference;
                        let thisfoi = variableDefinition.intended.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.referenceExposureIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                } else if (variableDefinition.variableRole === "outcome" || variableDefinition.variableRole === "measuredVariable") {
                    variableDefinitionMap.outcome = variableDefinition;
                    if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                        variableDefinitionMap.outcomeObservedReference = variableDefinition.observed.reference;
                        let thisfoi = variableDefinition.observed.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.outcomeObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                    if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                        variableDefinitionMap.outcomeIntendedReference = variableDefinition.intended.reference;
                        let thisfoi = variableDefinition.intended.reference.split("/")[1];
                        if (!isNaN(thisfoi)) {
                            variableDefinitionMap.outcomeIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                        }
                    }
                }

            }
            if (variableDefinition.variableRole.coding) {
                for (let codingIndex in variableDefinition.variableRole.coding) {
                    if (variableDefinition.variableRole.coding[codingIndex].code) {
                        if (variableDefinition.variableRole.coding[codingIndex].code === "population") {
                            variableDefinitionMap.population = variableDefinition;
                            if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                                variableDefinitionMap.populationObservedReference = variableDefinition.observed.reference;
                                let thisfoi = variableDefinition.observed.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.populationObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                            if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                                variableDefinitionMap.populationIntendedReference = variableDefinition.intended.reference;
                                let thisfoi = variableDefinition.intended.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.populationIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                        } else if (variableDefinition.variableRole.coding[codingIndex].code === "exposure") {
                            variableDefinitionMap.exposure = variableDefinition;
                            if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                                variableDefinitionMap.exposureObservedReference = variableDefinition.observed.reference;
                                let thisfoi = variableDefinition.observed.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.exposureObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                            if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                                variableDefinitionMap.exposureIntendedReference = variableDefinition.intended.reference;
                                let thisfoi = variableDefinition.intended.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.exposureIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                        } else if (variableDefinition.variableRole.coding[codingIndex].code === "referenceExposure") {
                            variableDefinitionMap.referenceExposure = variableDefinition;
                            if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                                variableDefinitionMap.referenceExposureObservedReference = variableDefinition.observed.reference;
                                let thisfoi = variableDefinition.observed.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.referenceExposureObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                            if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                                variableDefinitionMap.referenceExposureIntendedReference = variableDefinition.intended.reference;
                                let thisfoi = variableDefinition.intended.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.referenceExposureIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                        } else if (variableDefinition.variableRole.coding[codingIndex].code === "measuredVariable") {
                            variableDefinitionMap.outcome = variableDefinition;
                            if (variableDefinition.observed?.reference && typeof variableDefinition.observed.reference === "string") {
                                variableDefinitionMap.outcomeObservedReference = variableDefinition.observed.reference;
                                let thisfoi = variableDefinition.observed.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.outcomeObservedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                            if (variableDefinition.intended?.reference && typeof variableDefinition.intended.reference === "string") {
                                variableDefinitionMap.outcomeIntendedReference = variableDefinition.intended.reference;
                                let thisfoi = variableDefinition.intended.reference.split("/")[1];
                                if (!isNaN(thisfoi)) {
                                    variableDefinitionMap.outcomeIntendedResource = resourceCollection[thisfoi]?.revisedResourceJson;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return variableDefinitionMap;
};

const ModelCharacteristicDisplay = ({ modelCharactersticInstance }) => {
    let modelVariable = <></>;
    let modelVariableFound = false;
    if (modelCharactersticInstance.variable) {
        modelVariable = <>Adjusted for:<ul style={{ marginTop: "0px" }}>{modelCharactersticInstance.variable.map((variable, variableIndex) => {
            if (variable.variableDefinition) {
                modelVariableFound = true;
                let includeIfFound = false;
                let includeIfString;
                if (variable.extension?.length > 0) {
                    for (const extension of variable.extension) {
                        if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/statistic-model-include-if" ||
                            extension.url === "https://hl7.org/fhir/uv/ebm/StructureDefinition/statistic-model-include-if" ||
                            extension.url === "http://hl7.org/fhir/StructureDefinition/statistic-model-include-if") {
                            includeIfFound = true;
                            includeIfString = " {{ include if ";
                            for (const ext of extension.extension) {
                                if (ext.url === "attribute") {
                                    includeIfString += displayStringFromFHIRCodeableConcept(ext.valueCodeableConcept) || "";
                                    includeIfString += " IS "
                                } else if (ext.url === "value") {
                                    if (typeof ext.valueBoolean === 'boolean') {
                                        includeIfString += displayStringFromFHIRBoolean(ext.valueBoolean);
                                    } else if (ext.valueCodeableConcept) {
                                        includeIfString += displayStringFromFHIRCodeableConcept(ext.valueCodeableConcept);
                                    } else if (ext.valueQuantity) {
                                        includeIfString += displayStringFromFHIRQuantity(ext.valueQuantity);
                                    } else if (ext.valueRange) {
                                        includeIfString += displayStringFromFHIRRange(ext.valueRange);
                                    } else if (ext.valueExpression) {
                                        includeIfString += ext.valueExpression.name + " " + ext.valueExpression.description + " " + ext.valueExpression.expression;
                                    }
                                }
                            }
                            includeIfString += " }}";
                        }
                    }
                }
                return <li key={variableIndex}>
                    {variable.variableDefinition.display &&
                        <>{variable.variableDefinition.display}</>} {variable.handling &&
                            <>({typeof variable.handling === "string" ?
                                variable.handling
                                :
                                simpleDisplayStringFromFHIRCodeableConcept(variable.handling)})</>} {includeIfFound &&
                                    <>{includeIfString}</>}
                </li>
            } else {
                return <></>
            }
        })}</ul></>
    }
    let modelAttributeEstimate = <></>;
    let modelAttributeEstimateFound = false;
    if (modelCharactersticInstance.attribute || modelCharactersticInstance.attributeEstimate) {
        modelAttributeEstimate = <><p>Placeholder -- this model characteristic contains one or more attribute estimates</p></>;
        modelAttributeEstimateFound = true;
    }
    let includeIfFound = false;
    let includeIfExtension;
    if (modelCharactersticInstance.extension?.length > 0) {
        for (const extension of modelCharactersticInstance.extension) {
            if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/statistic-model-include-if" ||
                extension.url === "https://hl7.org/fhir/uv/ebm/StructureDefinition/statistic-model-include-if" ||
                extension.url === "http://hl7.org/fhir/StructureDefinition/statistic-model-include-if") {
                includeIfFound = true;
                includeIfExtension = {};
                for (const ext of extension.extension) {
                    if (ext.url === "attribute") {
                        includeIfExtension["attribute"] = ext.valueCodeableConcept || "";
                    } else if (ext.url === "value") {
                        if (typeof ext.valueBoolean === 'boolean') {
                            includeIfExtension["valueBoolean"] = ext.valueBoolean;
                        } else if (ext.valueCodeableConcept) {
                            includeIfExtension["valueCodeableConcept"] = ext.valueCodeableConcept;
                        } else if (ext.valueQuantity) {
                            includeIfExtension["valueQuantity"] = ext.valueQuantity;
                        } else if (ext.valueRange) {
                            includeIfExtension["valueRange"] = ext.valueRange;
                        } else if (ext.valueExpression) {
                            includeIfExtension["valueExpression"] = ext.valueExpression;
                        }
                    }
                }
            }
        }
    }
    return <>
        {includeIfFound && <div>
            <b>Include if: </b>
            <div style={{ marginLeft: "24px" }}>
                <div>
                    <b>Attribute: </b>
                    <DisplayFromFHIRCodeableConcept codeableConcept={includeIfExtension.attribute} />
                </div>
                <div>
                    <b>Value: </b>
                    {typeof includeIfExtension.valueBoolean === 'boolean' &&
                        displayStringFromFHIRBoolean(includeIfExtension.valueBoolean)}
                    {includeIfExtension.valueCodeableConcept &&
                        <DisplayFromFHIRCodeableConcept codeableConcept={includeIfExtension.valueCodeableConcept} />}
                    {includeIfExtension.valueQuantity &&
                        displayStringFromFHIRQuantity(includeIfExtension.valueQuantity)}
                    {includeIfExtension.valueRange &&
                        displayStringFromFHIRRange(includeIfExtension.valueRange)}
                    {includeIfExtension.valueExpression &&
                        <DisplayFromFHIRExpression expression={includeIfExtension.valueExpression} />}
                </div>
            </div>
        </div>}
        {modelCharactersticInstance.conditionCodeableConcept && <span>
            <b>If this condition is met: </b>
            <DisplayFromFHIRCodeableConcept codeableConcept={modelCharactersticInstance.conditionCodeableConcept} />
        </span>}
        {modelCharactersticInstance.conditionExpression && <span>
            <b>If this condition is met: </b>
            <DisplayFromFHIRExpression expression={modelCharactersticInstance.conditionExpression} />
        </span>}
        {modelCharactersticInstance.code && <span>
            <br />
            <b>Code: </b>
            <DisplayFromFHIRCodeableConcept codeableConcept={modelCharactersticInstance.code} />
        </span>}
        {modelCharactersticInstance.value && <span>
            <br />
            <b>Value: </b>
            {displayStringFromFHIRQuantity(modelCharactersticInstance.value)}
        </span>}
        {modelCharactersticInstance.valueQuantity && <span>
            <br />
            <b>Value: </b>
            {displayStringFromFHIRQuantity(modelCharactersticInstance.valueQuantity)}
        </span>}
        {modelCharactersticInstance.valueRange && <span>
            <br />
            <b>Value: </b>
            {displayStringFromFHIRRange(modelCharactersticInstance.valueRange)}
        </span>}
        {modelCharactersticInstance.valueCodeableConcept && <span>
            <br />
            <b>Value: </b>
            <DisplayFromFHIRCodeableConcept codeableConcept={modelCharactersticInstance.valueCodeableConcept} />
        </span>}
        {modelCharactersticInstance.intended && <p><b>Intended: </b>True</p>}
        {modelCharactersticInstance.applied && <p><b>Applied: </b>True</p>}
        {modelVariableFound && modelVariable}
        {modelAttributeEstimateFound && modelAttributeEstimate}
        {(Array.isArray(modelCharactersticInstance.modelCharacteristic) &&
            modelCharactersticInstance.modelCharacteristic.length > 0) &&
            modelCharactersticInstance.modelCharacteristic.map((item, itemIndex) => {
                return <div key={itemIndex}>
                    <p><b>Model Characteristic {itemIndex + 1} of {modelCharactersticInstance.modelCharacteristic.length}</b></p>
                    <ModelCharacteristicDisplay modelCharactersticInstance={item} />
                </div>
            })}
    </>
}

const certaintyRowProcess = (certaintyRows, certainty, level, parent, indexTrail) => {
    let certaintyRow = {
        "parent": parent, "indexTrail": indexTrail, "level": level, "typeCodes": "",
        "rating": "", "rater": [], "description": "", "notes": []
    };
    certaintyRow["typeCodes"] = getCodeableConceptDisplayForObjectOrArray(certainty.type);
    certaintyRow["rating"] = getCodeableConceptDisplayForObjectOrArray(certainty.rating);
    certaintyRow["description"] = certainty.description;
    if (certainty.rater) {
        if (Array.isArray(certainty.rater)) {
            certaintyRow["rater"] = certainty.rater;
        } else if (typeof certainty.rater === 'string' && certainty.rater !== "") {
            certaintyRow["rater"] = [certainty.rater];
        }
    }
    if (certainty.note) {
        if (Array.isArray(certainty.note)) {
            certaintyRow["notes"] = certainty.note;
        } else if (typeof certainty.note === 'object' && certainty.note !== null && certainty.note.text) {
            certaintyRow["notes"] = [certainty.note];
        }
    }
    certaintyRows.push(certaintyRow);
    if (certainty.subcomponent) {
        for (let subcomponentIndex in certainty.subcomponent) {
            let subcomponent = certainty.subcomponent[subcomponentIndex];
            certaintyRows = certaintyRowProcess(certaintyRows, subcomponent, level + 1, certaintyRow["typeCodes"], [...indexTrail, subcomponentIndex]);
        }
    }
    return certaintyRows;
}

const CertaintyRows = ({ certaintyEntries, level, editMode, state, stateSetter }) => {
    let certaintyRows = [];
    for (let certaintyIndex in certaintyEntries) {
        let certainty = certaintyEntries[certaintyIndex];
        certaintyRows = certaintyRowProcess(certaintyRows, certainty, level, "", [certaintyIndex]);
    }
    return certaintyRows.map((row, rowIndex) => {
        let typePaddingLeft = (24 * row.level).toString() + "px";
        //.toString(16)
        let rowShadeNumber = 255;
        rowShadeNumber -= (12 * row.level);
        if (rowShadeNumber < 155) {
            rowShadeNumber = 155;
        }
        rowShadeNumber = (rowShadeNumber).toString(16); //Convert it to hexadecimal
        let rowShade = "#" + rowShadeNumber + rowShadeNumber + rowShadeNumber;

        let description;
        let rater;
        let notes = row.notes;
        if (state) {
            //description = state.description;
            description = retrieveIterativeElement(state, "subcomponent", "description", row.indexTrail);
            rater = retrieveIterativeElement(state, "subcomponent", "rater", row.indexTrail);
        } else {
            description = row.description;
            rater = row.rater;
        }

        return <Table.Row key={rowIndex} style={{ backgroundColor: rowShade }}>
            <Table.Cell style={{ verticalAlign: "top" }}>
                {editMode ?
                    <span style={{ paddingLeft: typePaddingLeft, display: "flex" }}>
                        {row.parent && <>Subcomponent of {row.parent}:<br /></>}
                        {row.typeCodes}
                    </span>
                    :
                    <span style={{ paddingLeft: typePaddingLeft, display: "flex" }}>{row.typeCodes}</span>
                }
            </Table.Cell>
            <Table.Cell style={{ verticalAlign: "top" }}>{row.rating}</Table.Cell>
            <Table.Cell style={{ verticalAlign: "top" }}>
                {editMode ?
                    <TextField style={{ width: "100%" }} multiline className="inputField" type='text'
                        label={'Description'} size="small" variant='outlined' value={description || ''}
                        onChange={(e) => { editIterativeState(state, stateSetter, e.target.value, "subcomponent", "description", row.indexTrail) }} />
                    :
                    <>{description}</>
                }
            </Table.Cell>
            <Table.Cell style={{ verticalAlign: "top" }}>{notes.map((note, noteIndex) => {

                let noteValue;
                /*
                if (state) {
                  noteValue = retrieveIterativeElement(state, "subcomponent", "note", row.indexTrail, noteIndex, "text");
                } else {
                  noteValue = note.text;
                }
                */
                noteValue = note.text;
                return <p key={noteIndex}>
                    {noteValue && <>{noteValue}</>}
                </p>
            })}</Table.Cell>
            <Table.Cell style={{ verticalAlign: "top" }}>
                {editMode ?
                    <TextField style={{ width: "100%" }} multiline className="inputField"
                        type='text' label={'Rater'} size="small" variant='outlined' value={rater || ''}
                        onChange={(e) => {
                            editIterativeState(state, stateSetter, e.target.value, "subcomponent", "rater", row.indexTrail)
                        }} />
                    :
                    <>{Array.isArray(rater) ? rater.join(", ") : rater}</>
                }
            </Table.Cell>
        </Table.Row>
    })
}

const evidenceDotVariableDefinitionDotVariableRoleCodeSet = ["population", "exposure", "outcome", "covariate",
    "subpopulation", "referenceExposure", "measuredVariable", "confounder"];
const evidenceDotVariableDefinitionDotVariableRoleValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "population", display: "population" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "subpopulation", display: "subpopulation" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "exposure", display: "exposure" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "referenceExposure", display: "reference exposure" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "outcome", display: "outcome" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "measuredVariable", display: "measured variable" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "measuredVariableA", display: "measured variable A" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "measuredVariableB", display: "measured variable B" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "measuredVariableAB", display: "measured variable A to B" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "covariate", display: "covariate" },
    { system: "http://terminology.hl7.org/CodeSystem/variable-role", code: "confounder", display: "confounder" }
];
const evidenceDotVariableDefinitionDotReferenceResourceTypes = ['Group', 'EvidenceVariable'];
const evidenceDotVariableDefinitionDotDirectnessMatchValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/directness", version: "1.0.0", code: "low", display: "Low quality match between observed and intended variable" },
    { system: "http://terminology.hl7.org/CodeSystem/directness", version: "1.0.0", code: "moderate", display: "Moderate quality match between observed and intended variable" },
    { system: "http://terminology.hl7.org/CodeSystem/directness", version: "1.0.0", code: "high", display: "High quality match between observed and intended variable" },
    { system: "http://terminology.hl7.org/CodeSystem/directness", version: "1.0.0", code: "exact", display: "Exact match between observed and intended variable" }
];
const EvidenceVariableDefinitionEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    deletable, startCollapsed, containedResourceDictionary = {} }) => {

    const [evidenceVariableDefinitionState, setEvidenceVariableDefinitionState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingVariableDefinition = {
            description: "", note: [], variableRole: "", roleSubtype: "", comparatorCategory: "",
            observed: "", intended: "", directnessMatch: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingVariableDefinition.extension = startingValue.extension; }
            if (startingValue.description) { startingVariableDefinition.description = startingValue.description; }
            if (startingValue.note) { startingVariableDefinition.note = startingValue.note; }
            if (startingValue.variableRole) { startingVariableDefinition.variableRole = startingValue.variableRole; }
            if (startingValue.roleSubtype) { startingVariableDefinition.roleSubtype = startingValue.roleSubtype; }
            if (startingValue.comparatorCategory) { startingVariableDefinition.comparatorCategory = startingValue.comparatorCategory; }
            if (startingValue.observed) { startingVariableDefinition.observed = startingValue.observed; }
            if (startingValue.intended) { startingVariableDefinition.intended = startingValue.intended; }
            if (startingValue.directnessMatch) { startingVariableDefinition.directnessMatch = startingValue.directnessMatch; }
        }
        setEvidenceVariableDefinitionState(JSON.parse(JSON.stringify(startingVariableDefinition)));
    }, []);

    useEffect((() => {
        if (Object.keys(evidenceVariableDefinitionState).length > 0) {
            let newEvidenceVariableDefinition = {};
            if (evidenceVariableDefinitionState.extension) { newEvidenceVariableDefinition.extension = evidenceVariableDefinitionState.extension; }
            if (evidenceVariableDefinitionState.description) { newEvidenceVariableDefinition.description = evidenceVariableDefinitionState.description; }
            if (Array.isArray(evidenceVariableDefinitionState.note) && evidenceVariableDefinitionState.note.length > 0) {
                newEvidenceVariableDefinition.note = evidenceVariableDefinitionState.note;
            }
            if (evidenceVariableDefinitionState.variableRole) { newEvidenceVariableDefinition.variableRole = evidenceVariableDefinitionState.variableRole; }
            if (evidenceVariableDefinitionState.roleSubtype && Object.keys(evidenceVariableDefinitionState.roleSubtype).length > 0) {
                newEvidenceVariableDefinition.roleSubtype = evidenceVariableDefinitionState.roleSubtype;
            }
            if (evidenceVariableDefinitionState.comparatorCategory) { newEvidenceVariableDefinition.comparatorCategory = evidenceVariableDefinitionState.comparatorCategory; }

            if (evidenceVariableDefinitionState.observed && Object.keys(evidenceVariableDefinitionState.observed).length > 0) {
                newEvidenceVariableDefinition.observed = evidenceVariableDefinitionState.observed;
            }
            if (evidenceVariableDefinitionState.intended && Object.keys(evidenceVariableDefinitionState.intended).length > 0) {
                newEvidenceVariableDefinition.intended = evidenceVariableDefinitionState.intended;
            }
            if (evidenceVariableDefinitionState.directnessMatch && Object.keys(evidenceVariableDefinitionState.directnessMatch).length > 0) {
                newEvidenceVariableDefinition.directnessMatch = evidenceVariableDefinitionState.directnessMatch;
            }
            if (Object.keys(newEvidenceVariableDefinition).length === 0) {
                newEvidenceVariableDefinition = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEvidenceVariableDefinition }; });
        }
    }), [evidenceVariableDefinitionState]);

    if (startCollapsedState) {
        return <div>
            <p><b>{fieldLabel}: </b></p>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /></span>}
                <DisplayElementNotes element={startingValue.note} />
                {(startingValue.variableRole && typeof startingValue.variableRole === "string") &&
                    <><b>VARIABLE ROLE: </b>{startingValue.variableRole}<br /></>}
                {(startingValue.variableRole && typeof startingValue.variableRole === "object") &&
                    <><b>VARIABLE ROLE: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.variableRole} /><br /></>}
                {startingValue.roleSubtype && <><b>Variable Role Subtype: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.roleSubtype} /><br /></>}
                {startingValue.comparatorCategory && <><b>Comparator category: </b>{startingValue.comparatorCategory}<br /></>}
                {startingValue.observed && <><b>Observed: </b><DisplayFromFHIRReference reference={startingValue.observed}
                    containedResourceJson={startingValue.observed?.reference &&
                        containedResourceDictionary[startingValue.observed.reference]} /><br /></>}
                {startingValue.intended && <><b>Intended: </b><DisplayFromFHIRReference reference={startingValue.intended}
                    containedResourceJson={startingValue.intended?.reference &&
                        containedResourceDictionary[startingValue.intended.reference]} /><br /></>}
                {startingValue.directnessMatch && <><b>Directness match: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.directnessMatch} /><br /></>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(evidenceVariableDefinitionState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed
                    startingValue={evidenceVariableDefinitionState.description} setResourceState={setEvidenceVariableDefinitionState} />
                <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                    startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed
                    startingValue={evidenceVariableDefinitionState.note} setResourceState={setEvidenceVariableDefinitionState} />
                <CodeEntry elementName='variableRole' fieldLabel='Variable Role'
                    startingValue={evidenceVariableDefinitionState.variableRole}
                    allowedValues={evidenceDotVariableDefinitionDotVariableRoleCodeSet}
                    setResourceState={setEvidenceVariableDefinitionState} />
                <CodeableConceptEntry elementName='roleSubtype' fieldLabel='Variable Role Subtype'
                    startingValue={evidenceVariableDefinitionState.roleSubtype} startCollapsed
                    valueSet={evidenceDotVariableDefinitionDotVariableRoleValueSet}
                    setResourceState={setEvidenceVariableDefinitionState} />
                <StringEntry elementName='comparatorCategory' fieldLabel='Comparator Category'
                    startingValue={evidenceVariableDefinitionState.comparatorCategory}
                    setResourceState={setEvidenceVariableDefinitionState} />
                <ReferenceEntry elementName='observed' fieldLabel='Observed'
                    startingValue={evidenceVariableDefinitionState.observed} enableCreation={true}
                    startCollapsed
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={evidenceDotVariableDefinitionDotReferenceResourceTypes}
                    setResourceState={setEvidenceVariableDefinitionState} />
                <ReferenceEntry elementName='intended' fieldLabel='Intended'
                    startingValue={evidenceVariableDefinitionState.intended} enableCreation={true}
                    startCollapsed
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={evidenceDotVariableDefinitionDotReferenceResourceTypes}
                    setResourceState={setEvidenceVariableDefinitionState} />
                <CodeableConceptEntry elementName='directnessMatch' fieldLabel='Directness Match'
                    startingValue={evidenceVariableDefinitionState.directnessMatch}
                    startCollapsed
                    valueSet={evidenceDotVariableDefinitionDotDirectnessMatchValueSet}
                    setResourceState={setEvidenceVariableDefinitionState} />
                <br />     <br />
            </div>
        </>
    }
}

const EvidenceDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    let isStatisticModelProfile;
    let isEndpointAnalysisPlanProfile;
    let isComparativeEvidenceProfile;
    let isNonComparativeEvidenceProfile;
    let isSingleStudyEvidenceProfile;
    let isParticipantFlowEvidenceProfile;
    let isComparativeParticipantFlowEvidenceProfile;
    let isEvidenceSynthesisEvidenceProfile;
    let isNetEffectContributionProfile;
    let isNetEffectEstimateProfile;
    let isComparatorOnlyEvidenceProfile;
    let isInterventionOnlyEvidenceProfile;
    if (resourceJson.meta?.profile?.length) {
        for (const profile of resourceJson.meta.profile) {
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/statistic-model") {
                isStatisticModelProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/endpoint-analysis-plan") {
                isEndpointAnalysisPlanProfile = true;
                isStatisticModelProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-evidence") {
                isComparativeEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-baseline-measure-evidence") {
                isComparativeEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-participant-flow-evidence") {
                isComparativeEvidenceProfile = true;
                isComparativeParticipantFlowEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/non-comparative-evidence") {
                isNonComparativeEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/baseline-measure-evidence") {
                isNonComparativeEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/participant-flow-evidence") {
                isNonComparativeEvidenceProfile = true;
                isParticipantFlowEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/single-study-evidence") {
                isSingleStudyEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/evidence-synthesis-evidence") {
                isEvidenceSynthesisEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-contribution") {
                isComparativeEvidenceProfile = true;
                isNetEffectContributionProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-estimate") {
                isNetEffectEstimateProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparator-only-evidence") {
                isNonComparativeEvidenceProfile = true;
                isComparatorOnlyEvidenceProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/intervention-only-evidence") {
                isNonComparativeEvidenceProfile = true;
                isInterventionOnlyEvidenceProfile = true;
            }
        }
    }
    let toolDisplayName = "Evidence";
    if (isSingleStudyEvidenceProfile) { toolDisplayName = "SingleStudyEvidence"; }
    if (isEvidenceSynthesisEvidenceProfile) { toolDisplayName = "EvidenceSynthesisEvidence"; }
    if (isStatisticModelProfile) { toolDisplayName = "StatisticModel"; }
    if (isComparativeEvidenceProfile) { toolDisplayName = "ComparativeEvidence"; }
    if (isNonComparativeEvidenceProfile) { toolDisplayName = "NonComparativeEvidence"; }
    if (isComparatorOnlyEvidenceProfile) { toolDisplayName = "ComparatorOnlyEvidence"; }
    if (isInterventionOnlyEvidenceProfile) { toolDisplayName = "InterventionOnlyEvidence"; }
    if (isParticipantFlowEvidenceProfile) { toolDisplayName = "ParticipantFlowEvidence"; }
    if (isComparativeParticipantFlowEvidenceProfile) { toolDisplayName = "ComparativeParticipantFlowEvidence"; }
    if (isNetEffectContributionProfile) { toolDisplayName = "NetEffectContribution"; }
    if (isNetEffectEstimateProfile) { toolDisplayName = "NetEffectEstimate"; }
    if (isEndpointAnalysisPlanProfile) { toolDisplayName = "EndpointAnalysisPlan"; }

    let dataSourceList = [];
    let dataSource = "";
    let dataSourceURL = "";
    for (let relatedArtifactIndex in resourceJson.relatedArtifact) {
        let relatedArtifact = resourceJson.relatedArtifact[relatedArtifactIndex];
        if (relatedArtifact.label === "data source") {
            if (relatedArtifact.display) {
                dataSource = '{' + relatedArtifact.display + '}: ';
            }
            if (relatedArtifact.citation) {
                dataSource += relatedArtifact.citation;
            }
            if (relatedArtifact.url) {
                dataSourceURL = relatedArtifact.url;
            }
        }
        if (relatedArtifact.type === "derived-from") {
            if (relatedArtifact.document?.url) {
                relatedArtifact.targetUri = relatedArtifact.document.url;
                delete relatedArtifact.document;
            }
            if (relatedArtifact.document) {
                relatedArtifact.targetAttachment = relatedArtifact.document;
                delete relatedArtifact.document;
            }
            if (relatedArtifact.resource) {
                relatedArtifact.targetCanonical = relatedArtifact.resource;
                delete relatedArtifact.resource;
            }
            if (relatedArtifact.resourceReference) {
                relatedArtifact.targetReference = relatedArtifact.resourceReference;
                delete relatedArtifact.resourceReference;
            }
            if (relatedArtifact.citation) {
                relatedArtifact.targetMarkdown = relatedArtifact.citation;
                delete relatedArtifact.citation;
            }
            dataSourceList.push(relatedArtifact);
        }
    }
    for (let relatesToIndex in resourceJson.relatesTo) {
        let relatesTo = resourceJson.relatesTo[relatesToIndex];
        if (relatesTo.type === "derived-from" || relatesTo.type?.coding?.[0]?.code === "derived-from") {
            dataSourceList.push(relatesTo);
        }
    }

    let studyType = getCodeableConceptDisplayForObjectOrArray(resourceJson.studyType);
    let studyDesignList = [];
    if (resourceJson.studyDesign?.length > 0) {
        for (let studyDesignEntry in resourceJson.studyDesign) {
            studyDesignList.push(displayStringFromFHIRCodeableConcept(resourceJson.studyDesign[studyDesignEntry]));
        }
    }
    let studyDesign = studyDesignList.join('; ') || studyType || "";
    let synthesisTypeList = [];
    if (resourceJson.synthesisType) {
        if (Array.isArray(resourceJson.synthesisType)) {
            for (let synthesisTypeEntry in resourceJson.synthesisType) {
                synthesisTypeList.push(displayStringFromFHIRCodeableConcept(resourceJson.synthesisType[synthesisTypeEntry]));
            }
        } else {
            synthesisTypeList.push(displayStringFromFHIRCodeableConcept(resourceJson.synthesisType))
        }
    }
    let synthesisType = synthesisTypeList.join('; ') || "";

    const [evidenceResourceState, setEvidenceResourceState] = useState({});
    const [statisticsAvailable, setStatisticsAvailable] = useState(evidenceResourceState?.statistic?.length && (evidenceResourceState.statistic.length > 1 || Object.keys(evidenceResourceState.statistic[0]).length));
    const [variableDefinitionMap, setVariableDefinitionMap] = useState({});
    const [profileWarningsState, setProfileWarningsState] = useState({});
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR " + toolDisplayName + " - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setEvidenceResourceState(olderVersionJson);
                setStatisticsAvailable(olderVersionJson?.statistic?.length && (olderVersionJson.statistic.length > 1 || Object.keys(olderVersionJson.statistic[0]).length));
                dispatch(changeTopNavigationElements(["Summary", "Population", "Exposures", "Outcome",
                    "Study Design", "Statistics", "Certainty"]));
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            if (Object.keys(evidenceResourceState).length === 0) {
                setStatisticsAvailable(resourceJson?.statistic?.length && (resourceJson.statistic.length > 1 || Object.keys(resourceJson.statistic[0]).length));
            }
            setEvidenceResourceState(resourceJson);
            if (editMode) {
                dispatch(changeTopNavigationElements(["Summary", "Variable Definitions",
                    "Study Design", "Statistics", "Certainty"]));
            } else {
                dispatch(changeTopNavigationElements(["Summary", "Population", "Exposures", "Outcome",
                    "Study Design", "Statistics", "Certainty"]));
            }
            let variableDefinitionMapDictionary = getVariableDefinitionMap(resourceJson, resourceCollection);
            setVariableDefinitionMap(variableDefinitionMapDictionary);
            let variableDefinitionWarnings = [];
            if (isComparativeEvidenceProfile) {
                let populationFound;
                let exposureFound;
                let comparatorCategoryFound;
                let outcomeFound;
                if (resourceJson.variableDefinition) {
                    for (const varDef of resourceJson.variableDefinition) {
                        //console.log(varDef)
                        if (varDef.variableRole === "population") {
                            populationFound = true;
                        }
                        if (varDef.variableRole === "exposure") {
                            exposureFound = true;
                            if (varDef.comparatorCategory) {
                                comparatorCategoryFound = true;
                            }
                        }
                        if (varDef.variableRole === "outcome") {
                            outcomeFound = true;
                        }
                    }
                }
                if (!(populationFound && exposureFound && comparatorCategoryFound && outcomeFound)) {
                    let variableDefinitionWarning = "This Evidence Resource follows the ComparativeEvidence Profile.";
                    if (!populationFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'population' is expected."
                    }
                    if (!exposureFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'exposure' is expected."
                    }
                    if (!comparatorCategoryFound) {
                        variableDefinitionWarning += " A variableDefinition.comparatorCategory value is expected in the variableDefinition with variableRole = 'exposure'."
                    }
                    if (!outcomeFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'outcome' is expected."
                    }
                    variableDefinitionWarnings.push(variableDefinitionWarning);
                }
            }
            if (isNonComparativeEvidenceProfile) {
                let populationFound;
                let outcomeFound;
                if (resourceJson.variableDefinition) {
                    for (const varDef of resourceJson.variableDefinition) {
                        if (varDef.variableRole === "population") {
                            populationFound = true;
                        }
                        if (varDef.variableRole === "outcome") {
                            outcomeFound = true;
                        }
                    }
                }
                if (!(populationFound && outcomeFound)) {
                    let variableDefinitionWarning = "This Evidence Resource follows the NonComparativeEvidence Profile.";
                    if (!populationFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'population' is expected."
                    }
                    if (!outcomeFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'outcome' is expected."
                    }
                    variableDefinitionWarnings.push(variableDefinitionWarning);
                }
            }
            if (isParticipantFlowEvidenceProfile) {
                let intendedFound;
                let directnessMatchFound;
                if (resourceJson.variableDefinition) {
                    for (const varDef of resourceJson.variableDefinition) {
                        if (varDef.intended) {
                            intendedFound = true;
                        }
                        if (varDef.directnessMatch) {
                            directnessMatchFound = true;
                        }
                    }
                }
                if (intendedFound || directnessMatchFound) {
                    let variableDefinitionWarning = "This Evidence Resource follows the ParticipantFlowEvidence Profile.";
                    if (intendedFound) {
                        variableDefinitionWarning += " A variableDefinition has a value in the intended element and this is not expected."
                    }
                    if (directnessMatchFound) {
                        variableDefinitionWarning += " A variableDefinition has a value in the directnessMatch element and this is not expected."
                    }
                    variableDefinitionWarnings.push(variableDefinitionWarning);
                }
            }
            if (isComparativeParticipantFlowEvidenceProfile) {
                let intendedFound;
                let directnessMatchFound;
                if (resourceJson.variableDefinition) {
                    for (const varDef of resourceJson.variableDefinition) {
                        if (varDef.intended) {
                            intendedFound = true;
                        }
                        if (varDef.directnessMatch) {
                            directnessMatchFound = true;
                        }
                    }
                }
                if (intendedFound || directnessMatchFound) {
                    let variableDefinitionWarning = "This Evidence Resource follows the ComparativeParticipantFlowEvidence Profile.";
                    if (intendedFound) {
                        variableDefinitionWarning += " A variableDefinition has a value in the intended element and this is not expected.";
                    }
                    if (directnessMatchFound) {
                        variableDefinitionWarning += " A variableDefinition has a value in the directnessMatch element and this is not expected.";
                    }
                    variableDefinitionWarnings.push(variableDefinitionWarning);
                }
            }
            if (isNetEffectEstimateProfile) {
                let populationFound;
                let exposureFound;
                let comparatorCategoryFound;
                let intendedFound;
                if (resourceJson.variableDefinition) {
                    for (const varDef of resourceJson.variableDefinition) {
                        if (varDef.variableRole === "population") {
                            populationFound = true;
                            if (varDef.intended) {
                                intendedFound = true;
                            }
                        }
                        if (varDef.variableRole === "exposure") {
                            exposureFound = true;
                            if (varDef.comparatorCategory) {
                                comparatorCategoryFound = true;
                            }
                            if (varDef.intended) {
                                intendedFound = true;
                            }
                        }
                    }
                }
                if (!(populationFound && exposureFound && comparatorCategoryFound && !intendedFound)) {
                    let variableDefinitionWarning = "This Evidence Resource follows the NetEffectEstimate Profile.";
                    if (!populationFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'population' is expected.";
                    }
                    if (!exposureFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'exposure' is expected.";
                    }
                    if (!comparatorCategoryFound) {
                        variableDefinitionWarning += " A variableDefinition.comparatorCategory value is expected in the variableDefinition with variableRole = 'exposure'.";
                    }
                    if (intendedFound) {
                        variableDefinitionWarning += " A variableDefinition has a value in the intended element and this is not expected.";
                    }
                    variableDefinitionWarnings.push(variableDefinitionWarning);
                }
            }
            if (isNetEffectContributionProfile) {
                let covariateFound;
                let covariateRoleSubtypeMatches;
                let covariateObservedDisplayMatches;
                let relativeOutcomeImportanceFound;
                let covariateIntendedFound;
                if (resourceJson.variableDefinition) {
                    for (const varDef of resourceJson.variableDefinition) {
                        if (varDef.variableRole === "covariate") {
                            covariateFound = true;
                            if (varDef.roleSubtype?.text === "Relative Value Multiplier") {
                                covariateRoleSubtypeMatches = true;
                            }
                            if (varDef.observed?.display === "Relative Importance Multiplier") {
                                covariateObservedDisplayMatches = true;
                            }
                            if (varDef.intended) {
                                covariateIntendedFound = true;
                            }
                            if (varDef.extension?.[0].url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/relative-outcome-importance") {
                                relativeOutcomeImportanceFound = true;
                            }
                        }
                    }
                }
                if (!(covariateFound && covariateRoleSubtypeMatches && covariateObservedDisplayMatches &&
                    !covariateIntendedFound && relativeOutcomeImportanceFound)) {
                    let variableDefinitionWarning = "This Evidence Resource follows the NetEffectContribution Profile.";
                    if (!covariateFound) {
                        variableDefinitionWarning += " A variableDefinition with variableRole = 'covariate' is expected.";
                    }
                    if (!covariateRoleSubtypeMatches) {
                        variableDefinitionWarning += " A variableDefinition with the variableRole = 'covariate' is expected to have roleSubtype.text = 'Relative Value Multiplier'.";
                    }
                    if (!covariateObservedDisplayMatches) {
                        variableDefinitionWarning += " A variableDefinition with the variableRole = 'covariate' is expected to have observed.display = 'Relative Importance Multiplier'.";
                    }
                    if (covariateIntendedFound) {
                        variableDefinitionWarning += " A variableDefinition with the variableRole = 'covariate' has a value in the intended element and this is not expected."
                    }
                    if (!relativeOutcomeImportanceFound) {
                        variableDefinitionWarning += " A variableDefinition with the variableRole = 'covariate' is expected to have an extension using 'http://hl7.org/fhir/uv/ebm/StructureDefinition/relative-outcome-importance'.";
                    }
                    variableDefinitionWarnings.push(variableDefinitionWarning);
                }
            }
            if (variableDefinitionWarnings.length > 0) {
                setProfileWarningsState(prevState => {
                    return { ...prevState, "variableDefinition": variableDefinitionWarnings };
                });
            } else {
                setProfileWarningsState(prevState => {
                    return { ...prevState, "variableDefinition": null };
                });
            }
            let synthesisTypeWarning;
            if (isSingleStudyEvidenceProfile) {
                let notApplicableCodingFound;
                if (resourceJson.synthesisType?.coding) {
                    for (let coding of resourceJson.synthesisType.coding) {
                        if (coding.system === "http://terminology.hl7.org/CodeSystem/synthesis-type" &&
                            coding.code === "NotApplicable" && coding.display === "not applicable"
                        ) {
                            notApplicableCodingFound = true;
                        }
                    }
                }
                if (!notApplicableCodingFound) {
                    synthesisTypeWarning = "This Evidence Resource follows the SingleStudyEvidence Profile. A synthesisType value is expected to be http://terminology.hl7.org/CodeSystem/synthesis-type#NotApplicable 'not applicable'.";
                }
            }
            if (isEvidenceSynthesisEvidenceProfile && !resourceJson.synthesisType) {
                synthesisTypeWarning = "This Evidence Resource follows the EvidenceSynthesisEvidence Profile. A synthesisType value is expected.";
            }
            if (synthesisTypeWarning) {
                setProfileWarningsState(prevState => {
                    return { ...prevState, "synthesisType": synthesisTypeWarning };
                });
            } else {
                setProfileWarningsState(prevState => {
                    return { ...prevState, "synthesisType": null };
                });
            }
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: Evidence " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (editMode) {
            dispatch(changeTopNavigationElements(["Summary", "Variable Definitions",
                "Study Design", "Statistics", "Certainty"]));
            setStatisticsAvailable(evidenceResourceState?.statistic?.length && (evidenceResourceState.statistic.length > 1 || Object.keys(evidenceResourceState.statistic[0]).length));
        } else {
            dispatch(changeTopNavigationElements(["Summary", "Population", "Exposures", "Outcome",
                "Study Design", "Statistics", "Certainty"]));
        }
    }, [editMode]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(evidenceResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = newResourceDetail.revisedResourceJson.title || null;
            newResourceDetail.revisedResourceJson.title = evidenceResourceState.title;
            newResourceDetail.displayTitle = evidenceResourceState.title ||
                evidenceResourceState.name || newResourceDetail.displayTitle;
            if (evidenceResourceState.title !== undefined &&
                previousFinalValue !== evidenceResourceState.title) {
                console.log("resourceUpdate for Evidence.title");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [evidenceResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.note]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "assertion", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.assertion]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "variableDefinition", "ArrayOfEvidenceVariableDefinition", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.variableDefinition]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "synthesisType", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.synthesisType]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "studyDesign", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.studyDesign]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "statistic", "ArrayOfEvidenceStatistic", parentResourceDetail, dispatch);
        }
    }, [evidenceResourceState.statistic]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(evidenceResourceState, "certainty", "ArrayOfEvidenceCertainty", parentResourceDetail,
                dispatch);
        }
    }, [evidenceResourceState.certainty]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {evidenceResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="summary">Summary</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <StringEntry elementName='title' fieldLabel='Title'
                                    startingValue={evidenceResourceState.title} setResourceState={setEvidenceResourceState} />
                            </>
                            :
                            <>
                                {evidenceResourceState.title && <p><b>Title: </b>{evidenceResourceState.title}</p>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <MarkdownEntry elementName='description' fieldLabel='Description'
                                    startCollapsed={evidenceResourceState.description ? true : false}
                                    startingValue={evidenceResourceState.description}
                                    setResourceState={setEvidenceResourceState} />
                            </>
                            :
                            <>
                                {evidenceResourceState.description && <div>
                                    <b>Description: </b>
                                    <DisplayFromFHIRMarkdown markdown={evidenceResourceState.description} />
                                    <br /><br />
                                </div>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <MarkdownEntry elementName='assertion' fieldLabel='Assertion'
                                    startCollapsed={evidenceResourceState.assertion ? true : false}
                                    startingValue={evidenceResourceState.assertion}
                                    setResourceState={setEvidenceResourceState} />
                            </>
                            :
                            <>
                                {evidenceResourceState.assertion && <div>
                                    <b>Assertion: </b><DisplayFromFHIRMarkdown markdown={evidenceResourceState.assertion} />
                                    <br /><br />
                                </div>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                                    startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                                    startingValue={evidenceResourceState.note} setResourceState={setEvidenceResourceState} />
                            </>
                            :
                            <>
                                {Array.isArray(evidenceResourceState.note) &&
                                    evidenceResourceState.note.map((note, noteIndex) => {
                                        return <div key={noteIndex}>
                                            <b>Note: </b><DisplayFromFHIRAnnotation annotation={note} />
                                            <br /><br />
                                        </div>
                                    })}
                            </>
                    }
                    {
                        editMode ?
                            <></>
                            :
                            <>
                                {dataSource &&
                                    <p><b>Data Source: </b>{dataSource} {dataSourceURL && <>(<a href={dataSourceURL}
                                        target="_blank" rel="noopener noreferrer">{dataSourceURL}</a>)</>}</p>}
                                {dataSourceList.length > 0 && <>
                                    <p><b>Data Source(s): </b></p>
                                    {dataSourceList.map((source, sourceIndex) => {
                                        return <div key={sourceIndex}>
                                            <DisplayFromFHIRRelatesTo relatesTo={source}
                                                containedResourceDictionary={containedResourceDictionary} />
                                            <br />
                                        </div>
                                    })}</>
                                }
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <h4>Narrative Summary</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                        generatedNarrative={generateNarrativeForEvidenceResource(evidenceResourceState)}
                                        startCollapsed
                                        startingValue={evidenceResourceState.text} setResourceState={setEvidenceResourceState} />
                                </div>
                            </>
                            :
                            <>
                                {(evidenceResourceState.text?.div && evidenceResourceState.text.status && evidenceResourceState.text.status !== "empty") && <>
                                    <h4>Narrative Summary:</h4>
                                    <div style={{ marginLeft: "24px" }}>
                                        <DisplayFromFHIRXhtml xhtml={evidenceResourceState.text.div} />
                                    </div>
                                </>}
                            </>
                    }
                </div>
                {editMode ?
                    <>
                        <h3 id="variable-definitions">Variable Definitions</h3>
                        <div style={{ marginLeft: "24px" }}>
                            {profileWarningsState.variableDefinition &&
                                profileWarningsState.variableDefinition.map((warning, warningIndex) => {
                                    return <div key={warningIndex}>
                                        <p><b>WARNING: </b>{warning}</p>
                                    </div>
                                })}
                            <ArrayEntry datatype='EvidenceVariableDefinition' elementName='variableDefinition'
                                startCollapsed containedResourceDictionary={containedResourceDictionary}
                                fieldLabel='Variable Definition'
                                startingValue={evidenceResourceState.variableDefinition}
                                setResourceState={setEvidenceResourceState} />
                        </div>
                    </>
                    :
                    <>
                        <h3 id="population">Population</h3>
                        <div style={{ marginLeft: "24px" }}>
                            <br />
                            {variableDefinitionMap?.population && <>
                                {variableDefinitionMap.population.description &&
                                    <>
                                        <b>Description: </b><DisplayFromFHIRMarkdown markdown={variableDefinitionMap.population.description} />
                                        <br />
                                    </>}
                                {variableDefinitionMap.population.roleSubtype &&
                                    <>
                                        <b>Variable Role Subtype: </b><DisplayFromFHIRCodeableConcept codeableConcept={variableDefinitionMap.population.roleSubtype} />
                                        <br /></>}
                                {variableDefinitionMap.population.directnessMatch?.coding?.length > 0 &&
                                    <>
                                        <b>Directness match: </b>{variableDefinitionMap.population.directnessMatch.coding[0].display}
                                        <br /><br /></>}
                                <DisplayElementNotes element={variableDefinitionMap.population} />
                                {variableDefinitionMap.population.observed && <>
                                    <b>Observed Population</b>
                                    {variableDefinitionMap.populationObservedResource?.resourceType &&
                                        <>
                                            <div style={{ marginLeft: "24px" }}>
                                                <p>{variableDefinitionMap.populationObservedResource.title || variableDefinitionMap.populationObservedResource.name || ""}</p>

                                                {variableDefinitionMap.populationObservedResource.resourceType === "Group" &&
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.populationObservedResource} />
                                                }
                                                <br />
                                            </div>
                                        </>}
                                    <div style={{ marginLeft: "24px" }}>
                                        <b>Resource Reference</b>
                                        <div style={{ marginLeft: "24px" }}>
                                            <DisplayFromFHIRReference reference={variableDefinitionMap.population.observed}
                                                containedResourceJson={variableDefinitionMap.population.observed?.reference &&
                                                    containedResourceDictionary[variableDefinitionMap.population.observed.reference]} />
                                        </div>
                                    </div>
                                </>}
                                {variableDefinitionMap.population.intended && <>
                                    {variableDefinitionMap.population.observed && <br />}
                                    <b>Intended Population</b>
                                    {variableDefinitionMap.populationIntendedResource?.resourceType &&
                                        <>
                                            <div style={{ marginLeft: "24px" }}>
                                                <p> {variableDefinitionMap.populationIntendedResource.title || variableDefinitionMap.populationIntendedResource.name || ""}</p>

                                                {variableDefinitionMap.populationIntendedResource.resourceType === "Group" &&
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.populationIntendedResource} />
                                                }
                                                <br />
                                            </div>
                                        </>}
                                    <div style={{ marginLeft: "24px" }}>
                                        <b>Resource Reference</b>
                                        <div style={{ marginLeft: "24px" }}>
                                            <DisplayFromFHIRReference reference={variableDefinitionMap.population.intended}
                                                containedResourceJson={variableDefinitionMap.population.intended?.reference &&
                                                    containedResourceDictionary[variableDefinitionMap.population.intended.reference]} />
                                        </div>
                                    </div>
                                </>}
                            </>}
                            <br />
                        </div>
                        <h3 id="exposures">Exposures</h3>
                        <div style={{ marginLeft: "24px" }}>
                            {variableDefinitionMap?.exposure && <>
                                <b>Exposure</b>
                                <div style={{ marginLeft: "24px" }}>
                                    {variableDefinitionMap.exposure.description &&
                                        <>
                                            <b>Description: </b><DisplayFromFHIRMarkdown markdown={variableDefinitionMap.exposure.description} />
                                            <br /></>}
                                    {variableDefinitionMap.exposure.roleSubtype &&
                                        <>
                                            <b>Variable Role Subtype: </b><DisplayFromFHIRCodeableConcept codeableConcept={variableDefinitionMap.exposure.roleSubtype} />
                                            <br /></>}
                                    {(variableDefinitionMap.exposure.directnessMatch?.coding &&
                                        variableDefinitionMap.exposure.directnessMatch?.coding[0]) &&
                                        <><b>Directness match: </b>{variableDefinitionMap.exposure.directnessMatch.coding[0].display}
                                            <br /><br /></>}
                                    <DisplayElementNotes element={variableDefinitionMap.exposure} />
                                    {variableDefinitionMap.exposure.observed && <>
                                        <b>Observed Exposure</b>
                                        {variableDefinitionMap.exposureObservedResource?.resourceType &&
                                            <>
                                                <div style={{ marginLeft: "24px" }}>
                                                    <p>{variableDefinitionMap.exposureObservedResource.title || variableDefinitionMap.exposureObservedResource.name || ""}</p>
                                                </div>
                                                {variableDefinitionMap.exposureObservedResource.resourceType === "Group" ?
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.exposureObservedResource} />
                                                    :
                                                    <>
                                                        {variableDefinitionMap.exposureObservedResource.definition &&
                                                            <DisplayFromFHIRCodeableReference
                                                                codeableReference={variableDefinitionMap.exposureObservedResource.definition}
                                                                containedResourceDictionary={containedResourceDictionary} />}
                                                        {variableDefinitionMap.exposureObservedResource.handling &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Handling:</b> {variableDefinitionMap.exposureObservedResource.handling}
                                                                <br />
                                                            </div>}
                                                        {variableDefinitionMap.exposureObservedResource.category &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Categories:</b>
                                                                <br />
                                                                {variableDefinitionMap.exposureObservedResource.category.map((categoryInstance, index) => {
                                                                    return <div key={index}>
                                                                        <b>o </b>{categoryInstance.name}
                                                                    </div>
                                                                })}
                                                                <br />
                                                            </div>}
                                                    </>
                                                }
                                                <br />
                                            </>}
                                        <div style={{ marginLeft: "24px" }}>
                                            <b>Resource Reference</b>
                                            <div style={{ marginLeft: "24px" }}>
                                                <DisplayFromFHIRReference reference={variableDefinitionMap.exposure.observed}
                                                    containedResourceJson={variableDefinitionMap.exposure.observed?.reference &&
                                                        containedResourceDictionary[variableDefinitionMap.exposure.observed.reference]} />
                                            </div>
                                        </div>
                                    </>}
                                    {variableDefinitionMap.exposure.intended && <>
                                        {variableDefinitionMap.exposure.observed && <br />}
                                        <b>Intended Exposure</b>
                                        {variableDefinitionMap.exposureIntendedResource?.resourceType &&
                                            <>
                                                <div style={{ marginLeft: "24px" }}>
                                                    <p>{variableDefinitionMap.exposureIntendedResource.title || variableDefinitionMap.exposureIntendedResource.name || ""}</p>
                                                </div>
                                                {variableDefinitionMap.exposureIntendedResource.resourceType === "Group" ?
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.exposureIntendedResource} />
                                                    :
                                                    <>
                                                        {variableDefinitionMap.exposureIntendedResource.definition &&
                                                            <DisplayFromFHIRCodeableReference
                                                                codeableReference={variableDefinitionMap.exposureIntendedResource.definition}
                                                                containedResourceDictionary={containedResourceDictionary} />}
                                                        {variableDefinitionMap.exposureIntendedResource.handling && <div style={{ marginLeft: "24px" }}><b>Handling:</b> {variableDefinitionMap.exposureIntendedResource.handling}<br /></div>}
                                                        {variableDefinitionMap.exposureIntendedResource.category &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Categories:</b>
                                                                <br />
                                                                {variableDefinitionMap.exposureIntendedResource.category.map((categoryInstance, index) => {
                                                                    return <div key={index}>
                                                                        <b>o </b>{categoryInstance.name}
                                                                    </div>
                                                                })}
                                                                <br />
                                                            </div>}
                                                    </>
                                                }
                                                <br />
                                            </>}
                                        <div style={{ marginLeft: "24px" }}>
                                            <b>Resource Reference</b>
                                            <div style={{ marginLeft: "24px" }}>
                                                <DisplayFromFHIRReference reference={variableDefinitionMap.exposure.intended}
                                                    containedResourceJson={variableDefinitionMap.exposure.intended?.reference &&
                                                        containedResourceDictionary[variableDefinitionMap.exposure.intended.reference]} />
                                            </div>
                                        </div>
                                    </>}
                                </div>
                                {variableDefinitionMap.exposure.comparatorCategory && <><br />
                                    <b>Control Exposure</b>
                                    <div style={{ marginLeft: "24px" }}>
                                        {variableDefinitionMap.exposure.comparatorCategory}
                                    </div>
                                </>}
                            </>}
                            {variableDefinitionMap?.referenceExposure && <><br />
                                <b>Control Exposure</b>
                                <div style={{ marginLeft: "24px" }}>
                                    {variableDefinitionMap.referenceExposure.description && <><b>Description: </b><DisplayFromFHIRMarkdown markdown={variableDefinitionMap.referenceExposure.description} /><br /></>}
                                    {variableDefinitionMap.referenceExposure.directnessMatch?.coding?.length > 0 && <><b>Directness match: </b>{variableDefinitionMap.referenceExposure.directnessMatch.coding[0].display}<br /><br /></>}
                                    <DisplayElementNotes element={variableDefinitionMap.referenceExposure} />
                                    {variableDefinitionMap.referenceExposure.observed && <>
                                        <b>Observed Reference Exposure</b>
                                        {variableDefinitionMap.referenceExposureObservedResource?.resourceType &&
                                            <>
                                                <div style={{ marginLeft: "24px" }}>
                                                    <p>{variableDefinitionMap.referenceExposureObservedResource.title || variableDefinitionMap.referenceExposureObservedResource.name || ""}</p></div>
                                                {variableDefinitionMap.referenceExposureObservedResource.resourceType === "Group" ?
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.referenceExposureObservedResource} />
                                                    :
                                                    <>
                                                        {variableDefinitionMap.referenceExposureObservedResource.definition &&
                                                            <DisplayFromFHIRCodeableReference
                                                                containedResourceDictionary={containedResourceDictionary}
                                                                codeableReference={variableDefinitionMap.referenceExposureObservedResource.definition} />}
                                                        {variableDefinitionMap.referenceExposureObservedResource.handling && <div style={{ marginLeft: "24px" }}><b>Handling:</b> {variableDefinitionMap.referenceExposureObservedResource.handling}<br /></div>}
                                                        {variableDefinitionMap.referenceExposureObservedResource.category &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Categories:</b>
                                                                <br />
                                                                {variableDefinitionMap.referenceExposureObservedResource.category.map((categoryInstance, index) => {
                                                                    return <div key={index}>
                                                                        <b>o </b>{categoryInstance.name}
                                                                    </div>
                                                                })}
                                                                <br />
                                                            </div>}
                                                    </>
                                                }
                                                <br />
                                            </>}
                                        <div style={{ marginLeft: "24px" }}>
                                            <b>Resource Reference</b>
                                            <div style={{ marginLeft: "24px" }}>
                                                <DisplayFromFHIRReference reference={variableDefinitionMap.referenceExposure.observed}
                                                    containedResourceJson={variableDefinitionMap.referenceExposure.observed?.reference &&
                                                        containedResourceDictionary[variableDefinitionMap.referenceExposure.observed.reference]} />
                                            </div>
                                        </div>
                                    </>}
                                    {variableDefinitionMap.referenceExposure.intended && <>
                                        {variableDefinitionMap.referenceExposure.observed && <br />}
                                        <b>Intended Reference Exposure</b>
                                        {variableDefinitionMap.referenceExposureIntendedResource?.resourceType &&
                                            <>
                                                <div style={{ marginLeft: "24px" }}>
                                                    <p>{variableDefinitionMap.referenceExposureIntendedResource.title || variableDefinitionMap.referenceExposureIntendedResource.name || ""}</p></div>
                                                {variableDefinitionMap.referenceExposureIntendedResource.resourceType === "Group" ?
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.referenceExposureIntendedResource} />
                                                    :
                                                    <>
                                                        {variableDefinitionMap.referenceExposureIntendedResource.definition &&
                                                            <DisplayFromFHIRCodeableReference
                                                                containedResourceDictionary={containedResourceDictionary}
                                                                codeableReference={variableDefinitionMap.referenceExposureIntendedResource.definition} />}
                                                        {variableDefinitionMap.referenceExposureIntendedResource.handling && <div style={{ marginLeft: "24px" }}><b>Handling:</b> {variableDefinitionMap.referenceExposureIntendedResource.handling}<br /></div>}
                                                        {variableDefinitionMap.referenceExposureIntendedResource.category &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Categories:</b>
                                                                <br />
                                                                {variableDefinitionMap.referenceExposureIntendedResource.category.map((categoryInstance, index) => {
                                                                    return <div key={index}>
                                                                        <b>o </b>{categoryInstance.name}
                                                                    </div>
                                                                })}
                                                                <br />
                                                            </div>}

                                                    </>
                                                }
                                                <br />
                                            </>}
                                        <div style={{ marginLeft: "24px" }}>
                                            <b>Resource Reference</b>
                                            <div style={{ marginLeft: "24px" }}>
                                                <DisplayFromFHIRReference reference={variableDefinitionMap.referenceExposure.intended}
                                                    containedResourceJson={variableDefinitionMap.referenceExposure.intended?.reference &&
                                                        containedResourceDictionary[variableDefinitionMap.referenceExposure.intended.reference]} />
                                            </div>
                                        </div>
                                    </>}
                                </div>
                            </>}
                            <br />
                        </div>
                        <h3 id="outcome">Outcome</h3>
                        <div style={{ marginLeft: "24px" }}>
                            <br />
                            {variableDefinitionMap?.outcome &&
                                <div>
                                    {variableDefinitionMap.outcome.description &&
                                        <><b>Description: </b><DisplayFromFHIRMarkdown markdown={variableDefinitionMap.outcome.description} /><br /></>}
                                    {variableDefinitionMap.outcome.roleSubtype &&
                                        <><b>Variable Role Subtype: </b><DisplayFromFHIRCodeableConcept codeableConcept={variableDefinitionMap.outcome.roleSubtype} /><br /></>}
                                    {variableDefinitionMap.outcome.directnessMatch?.coding?.length > 0 && <><b>Directness match: </b>{variableDefinitionMap.outcome.directnessMatch.coding[0].display}<br /><br /></>}
                                    <DisplayElementNotes element={variableDefinitionMap.outcome} />
                                    {variableDefinitionMap.outcome.observed && <>
                                        <b>Observed Outcome</b>
                                        {variableDefinitionMap.outcomeObservedResource?.resourceType &&
                                            <>
                                                <div style={{ marginLeft: "24px" }}>
                                                    <p>{variableDefinitionMap.outcomeObservedResource.title || variableDefinitionMap.outcomeObservedResource.name || ""}</p>
                                                </div>
                                                {variableDefinitionMap.outcomeObservedResource.resourceType === "Group" ?
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        containedResourceDictionary={containedResourceDictionary}
                                                        fhirJson={variableDefinitionMap.outcomeObservedResource} />
                                                    :
                                                    <>
                                                        {variableDefinitionMap.outcomeObservedResource.definition &&
                                                            <DisplayFromFHIRCodeableReference
                                                                containedResourceDictionary={containedResourceDictionary}
                                                                codeableReference={variableDefinitionMap.outcomeObservedResource.definition} />}
                                                        {variableDefinitionMap.outcomeObservedResource.handling && <div style={{ marginLeft: "24px" }}><b>Handling:</b> {variableDefinitionMap.outcomeObservedResource.handling}<br /></div>}
                                                        {variableDefinitionMap.outcomeObservedResource.category &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Categories:</b>
                                                                <br />
                                                                {variableDefinitionMap.outcomeObservedResource.category.map((categoryInstance, index) => {
                                                                    return <div key={index}>
                                                                        <b>o </b>{categoryInstance.name}
                                                                    </div>
                                                                })}
                                                                <br />
                                                            </div>}
                                                    </>
                                                }
                                                <br />
                                            </>}
                                        <div style={{ marginLeft: "24px" }}>
                                            <b>Resource Reference</b>
                                            <div style={{ marginLeft: "24px" }}>
                                                <DisplayFromFHIRReference reference={variableDefinitionMap.outcome.observed}
                                                    containedResourceJson={variableDefinitionMap.outcome.observed?.reference &&
                                                        containedResourceDictionary[variableDefinitionMap.outcome.observed.reference]} />
                                            </div>
                                        </div>
                                    </>}
                                    {variableDefinitionMap.outcome.intended && <>
                                        {variableDefinitionMap.outcome.observed && <br />}
                                        <b>Intended Outcome</b>
                                        {variableDefinitionMap.outcomeIntendedResource?.resourceType &&
                                            <>
                                                <div style={{ marginLeft: "24px" }}>
                                                    <p>{variableDefinitionMap.outcomeIntendedResource.title || variableDefinitionMap.outcomeIntendedResource.name || ""}</p>
                                                </div>
                                                {variableDefinitionMap.outcomeIntendedResource.resourceType === "Group" ?
                                                    <ManageInclusionExclusionEnhancedCharacteristicTables editMode={false}
                                                        fhirJson={variableDefinitionMap.outcomeIntendedResource}
                                                        containedResourceDictionary={containedResourceDictionary} />
                                                    :
                                                    <>
                                                        {variableDefinitionMap.outcomeIntendedResource.definition &&
                                                            <DisplayFromFHIRCodeableReference
                                                                containedResourceDictionary={containedResourceDictionary}
                                                                codeableReference={variableDefinitionMap.outcomeIntendedResource.definition} />}
                                                        {variableDefinitionMap.outcomeIntendedResource.handling && <div style={{ marginLeft: "24px" }}><b>Handling:</b> {variableDefinitionMap.outcomeIntendedResource.handling}<br /></div>}
                                                        {variableDefinitionMap.outcomeIntendedResource.category &&
                                                            <div style={{ marginLeft: "24px" }}>
                                                                <b>Categories:</b>
                                                                <br />
                                                                {variableDefinitionMap.outcomeIntendedResource.category.map((categoryInstance, index) => {
                                                                    return <div key={index}>
                                                                        <b>o </b>{categoryInstance.name}
                                                                    </div>
                                                                })}
                                                                <br />
                                                            </div>}
                                                    </>
                                                }
                                                <br />
                                            </>}
                                        <div style={{ marginLeft: "24px" }}>
                                            <b>Resource Reference</b>
                                            <div style={{ marginLeft: "24px" }}>
                                                <DisplayFromFHIRReference reference={variableDefinitionMap.outcome.intended}
                                                    containedResourceJson={variableDefinitionMap.outcome.intended?.reference &&
                                                        containedResourceDictionary[variableDefinitionMap.outcome.intended.reference]} />
                                            </div>
                                        </div>
                                    </>}
                                </div>
                            }
                            <br />
                        </div>
                    </>
                }
                <h3 id="study-design">Study Design</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <div>
                            <ArrayEntry datatype='CodeableConcept' elementName='studyDesign'
                                fieldLabel='Study Design'
                                startingValue={evidenceResourceState.studyDesign || null} startCollapsed
                                codeableConceptLevelValueSet={studyDesignCodeableConceptSet}
                                valueSet={SEVCO.studyDesign} setResourceState={setEvidenceResourceState} />
                            {profileWarningsState?.synthesisType &&
                                <p><b>WARNING: </b>{profileWarningsState.synthesisType}</p>}
                            <ArrayEntry datatype='CodeableConcept' elementName='synthesisType'
                                fieldLabel='Synthesis Type'
                                startingValue={evidenceResourceState.synthesisType || null} startCollapsed
                                startEmptyArrayClosed
                                valueSet={evidenceDotSynthesisTypeValueSet}
                                setResourceState={setEvidenceResourceState} />
                        </div>
                        :
                        <>
                            <span><b>Synthesis Type: </b> {synthesisType ? synthesisType : <>[Not Reported]</>}</span>
                            <br />
                            <span><b>Study Design: </b> {studyDesign ? studyDesign : <>[Not Reported]</>}</span>
                        </>
                    }
                </div>
                <h3 id="statistics">Statistics</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            {statisticsAvailable ?
                                <ArrayEntry datatype='EvidenceStatistic' elementName='statistic'
                                    containedResourceDictionary={containedResourceDictionary}
                                    fieldLabel='Statistic' startingValue={evidenceResourceState.statistic}
                                    isStatisticModelProfile={isStatisticModelProfile}
                                    isEndpointAnalysisPlanProfile={isEndpointAnalysisPlanProfile}
                                    isParticipantFlowEvidenceProfile={isParticipantFlowEvidenceProfile}
                                    isNetEffectContributionProfile={isNetEffectContributionProfile}
                                    isNetEffectEstimateProfile={isNetEffectEstimateProfile}
                                    startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                                    setResourceState={setEvidenceResourceState} />
                                :
                                <InitialStatisticEntry resourceState={evidenceResourceState} setResourceState={setEvidenceResourceState} setStatisticsAvailable={setStatisticsAvailable} />
                            }
                        </>
                        :
                        <>
                            {Array.isArray(evidenceResourceState.statistic) &&
                                evidenceResourceState.statistic.map((statistic, statisticIndex) => {
                                    let statisticTypeDisplay = "";
                                    if (statistic.statisticType) {
                                        if (statistic.statisticType.coding?.length > 0) {
                                            if (statistic.statisticType.coding[0].display) {
                                                statisticTypeDisplay = statistic.statisticType.coding[0].display;
                                            } else if (statistic.statisticType.coding[0].code) {
                                                statisticTypeDisplay = statistic.statisticType.coding[0].code;
                                            }
                                        }
                                        if (statistic.statisticType.text) {
                                            statisticTypeDisplay += "   " + statistic.statisticType.text;
                                        }
                                    }
                                    let statisticValue = "";
                                    if (statistic.quantity) {
                                        let comparator = "";
                                        if (statistic.quantity.comparator) {
                                            comparator = statistic.quantity.comparator + " ";
                                        }
                                        let unit = "";
                                        if (statistic.quantity.unit) {
                                            unit = " " + statistic.quantity.unit;
                                        }
                                        let value = statistic.quantity.value;
                                        if (value && !unit && statisticTypeDisplay.replaceAll("   ", "").toLowerCase() === "percentage") {
                                            unit = "%";
                                        }
                                        statisticValue = comparator + value + unit;
                                    }
                                    let sampleSizeQuantified = "";
                                    if (statistic.sampleSize) {
                                        if (statistic.sampleSize.numberOfStudies) {
                                            sampleSizeQuantified = statistic.sampleSize.numberOfStudies + " studies";
                                        }
                                        if (statistic.sampleSize.numberOfParticipants) {
                                            if (sampleSizeQuantified) {
                                                sampleSizeQuantified += ", ";
                                            }
                                            sampleSizeQuantified += statistic.sampleSize.numberOfParticipants + " participants";
                                        }
                                        if (statistic.sampleSize.numberAnalyzed) {
                                            if (sampleSizeQuantified) {
                                                sampleSizeQuantified += ", ";
                                            }
                                            sampleSizeQuantified += statistic.sampleSize.numberAnalyzed + " analyzed";
                                        }
                                        if (statistic.sampleSize.knownDataCount) {
                                            if (sampleSizeQuantified) {
                                                sampleSizeQuantified += ", ";
                                            }
                                            sampleSizeQuantified += statistic.sampleSize.knownDataCount + " without missing data";
                                        }
                                    }

                                    return <div key={statisticIndex}>
                                        <br />
                                        {evidenceResourceState.statistic.length > 1 &&
                                            <b style={{ marginBottom: "0px" }}>Statistic #{statisticIndex + 1}</b>}
                                        {statistic.category &&
                                            <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<b>Category:</b> {simpleDisplayStringFromFHIRCodeableConcept(statistic.category)}</span>}
                                        <div style={{ marginLeft: "24px" }}>
                                            {statistic.description &&
                                                <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={statistic.description} /><br /></span>}
                                            {Array.isArray(statistic.note) &&
                                                statistic.note.map((note, noteIndex) => {
                                                    return <span key={noteIndex}><b>Note:</b> {note.text}<br /></span>
                                                })}
                                            <Table style={{ margin: "4px", width: "600px" }}>
                                                <Table.Body>
                                                    <Table.Row>
                                                        <Table.Cell style={{ width: "280px", paddingTop: "6px", paddingBottom: "6px", paddingLeft: "12px" }}>
                                                            <b>{statisticTypeDisplay}</b>
                                                        </Table.Cell>
                                                        <Table.Cell style={{ paddingTop: "6px", paddingBottom: "6px", paddingLeft: "12px" }}>
                                                            <b>{statisticValue}</b>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                </Table.Body>
                                            </Table>
                                            {!isNaN(statistic.numberOfEvents) &&
                                                <span><b>Number of Events: </b>{statistic.numberOfEvents}<br /></span>}
                                            {!isNaN(statistic.numberAffected) &&
                                                <span><b>Number Affected: </b>{statistic.numberAffected}<br /></span>}
                                            {statistic.sampleSize && <><b>Sample Size: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {sampleSizeQuantified && <><b></b>{sampleSizeQuantified}<br /></>}
                                                    {statistic.sampleSize.description &&
                                                        <span><b>Sample Size Description: </b><DisplayFromFHIRMarkdown markdown={statistic.sampleSize.description} /><br /></span>}
                                                    <DisplayElementNotes element={statistic.sampleSize} />
                                                </div>
                                            </>}
                                            {statistic.attributeEstimate?.length > 0 &&
                                                <Table style={{ margin: "4px" }}>
                                                    <Table.Header><Table.Row>
                                                        <Table.HeaderCell className={"leftAlignedCell"}
                                                            style={{ padding: "6px", width: "360px" }}>Attribute</Table.HeaderCell>
                                                        <Table.HeaderCell style={{ padding: "6px" }}>Value</Table.HeaderCell>
                                                        <Table.HeaderCell style={{ padding: "6px" }}>Notes</Table.HeaderCell>
                                                    </Table.Row></Table.Header>
                                                    <Table.Body>
                                                        {statistic.attributeEstimate.map((attributeEstimate, aeIndex) => {
                                                            let calculatedPercentageAttribute = false;
                                                            let attribute = "";
                                                            if (attributeEstimate.level) {
                                                                attribute = 100 * attributeEstimate.level + "% ";
                                                            }
                                                            if (attributeEstimate.type) {
                                                                if (attributeEstimate.type.coding?.length > 0) {
                                                                    if (attributeEstimate.type.coding[0].display) {
                                                                        attribute += attributeEstimate.type.coding[0].display;
                                                                    } else if (attributeEstimate.type.coding[0].code) {
                                                                        attribute += attributeEstimate.type.coding[0].code;
                                                                    }
                                                                }
                                                                if (attributeEstimate.type.text) {
                                                                    if (attributeEstimate.type.text.toLowerCase() === "percentage") {
                                                                        calculatedPercentageAttribute = true;
                                                                    }
                                                                    attribute += " " + attributeEstimate.type.text;
                                                                }
                                                            }
                                                            let attributeValue = "";
                                                            if (attributeEstimate.quantity) {
                                                                let comparator = "";
                                                                if (attributeEstimate.quantity.comparator) {
                                                                    comparator = attributeEstimate.quantity.comparator + " ";
                                                                }
                                                                let unit = "";
                                                                if (attributeEstimate.quantity.unit) {
                                                                    unit = " " + attributeEstimate.quantity.unit;
                                                                }
                                                                let attributeEstimateValue = "";
                                                                if (attributeEstimate.quantity.value !== undefined) {
                                                                    attributeEstimateValue = attributeEstimate.quantity.value
                                                                }
                                                                attributeValue = comparator + attributeEstimate.quantity.value + unit;
                                                                if (calculatedPercentageAttribute && attributeEstimate.quantity.value && (comparator === "" || comparator === " ") && attributeEstimate.quantity.unit === "%") {
                                                                    try {
                                                                        //The plus symbol belongs here, +parseFloat(4).toFixed(2) === 4 as an int vs parseFloat(4).toFixed(2) === "4.00" as a string
                                                                        attributeEstimateValue = +parseFloat(attributeEstimate.quantity.value).toFixed(2);
                                                                        if (attributeEstimateValue.toString() !== "NaN") {
                                                                            attributeValue = attributeEstimateValue + unit;
                                                                        }
                                                                    } catch (e) {
                                                                        console.log("ERROR in EvidenceDisplay statistic.attributeEstimate.map");
                                                                        console.log(e);
                                                                    }
                                                                }
                                                            }
                                                            if (attributeValue === "" && attributeEstimate.range) {
                                                                let lowValue = "No Lower Bound";
                                                                let highValue = "No Upper Bound";
                                                                if (attributeEstimate.range.low?.value !== undefined) {
                                                                    lowValue = attributeEstimate.range.low.value;
                                                                    if (attributeEstimate.range.low.unit) {
                                                                        lowValue += " " + attributeEstimate.range.low.unit;
                                                                    }
                                                                }
                                                                if (attributeEstimate.range.high?.value !== undefined) {
                                                                    highValue = attributeEstimate.range.high.value;
                                                                    if (attributeEstimate.range.high.unit) {
                                                                        highValue += " " + attributeEstimate.range.high.unit;
                                                                    }
                                                                }
                                                                attributeValue = lowValue + " to " + highValue;
                                                            }
                                                            return <Table.Row key={aeIndex}>
                                                                <Table.Cell>{attribute}</Table.Cell>
                                                                <Table.Cell>{attributeValue}</Table.Cell>
                                                                <Table.Cell>
                                                                    {attributeEstimate.description &&
                                                                        <><b>Description: </b><DisplayFromFHIRMarkdown markdown={attributeEstimate.description} /><br /></>}
                                                                    <DisplayElementNotes element={attributeEstimate.note} />
                                                                </Table.Cell>
                                                            </Table.Row>
                                                        }
                                                        )}
                                                    </Table.Body>
                                                </Table>}
                                            {(Array.isArray(statistic.modelCharacteristic) &&
                                                statistic.modelCharacteristic.length > 0) &&
                                                statistic.modelCharacteristic.map((item, itemIndex) => {
                                                    return <div key={itemIndex}>
                                                        <p><b>Model Characteristic {itemIndex + 1} of {statistic.modelCharacteristic.length}</b></p>
                                                        <div style={{ marginLeft: "24px" }}>
                                                            <ModelCharacteristicDisplay modelCharactersticInstance={item} />
                                                        </div>
                                                    </div>
                                                })}
                                        </div>
                                        <br />
                                    </div>
                                })}
                        </>
                    }
                </div>
                <h3 id="certainty">Certainty</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <ArrayEntry datatype='EvidenceCertainty' elementName='certainty'
                                fieldLabel='Certainty' startEmptyArrayClosed={true}
                                deletable={true} deletableArray={true} startCollapsed={true}
                                startingValue={evidenceResourceState.certainty}
                                setResourceState={setEvidenceResourceState} />
                        </>
                        :
                        <>
                            {evidenceResourceState.certainty?.length > 0 &&
                                <Table style={{ margin: "4px" }}>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell style={{ padding: "6px" }}>Type</Table.HeaderCell>
                                            <Table.HeaderCell style={{ padding: "6px" }}>Rating</Table.HeaderCell>
                                            <Table.HeaderCell style={{ padding: "6px", width: "25%" }}>Description</Table.HeaderCell>
                                            <Table.HeaderCell style={{ padding: "6px", width: "25%" }}>Notes</Table.HeaderCell>
                                            <Table.HeaderCell style={{ padding: "6px" }}>Rater</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        <CertaintyRows certaintyEntries={evidenceResourceState.certainty} level={0} editMode={false} />
                                    </Table.Body>
                                </Table>
                            }
                        </>
                    }
                </div>
            </div>
        </>}
    </div>
}

export default EvidenceDisplay;
export { EvidenceVariableDefinitionEntry, ModelCharacteristicDisplay, CertaintyRows };